<template>
    <b-container fluid>
        <b-row>
            <b-col offset-xl="1" xl="10">
                <b-card body-class="p-0" header-class="border-0">
                    <template v-slot:header>
                        <b-row align-v="center">
                            <b-col>
                                <h3 class="mb-0">Compensating Factors</h3>
                            </b-col>
                        </b-row>
                    </template>

                    <!-- Compensating Factor Table -->
                    <el-table
                        class="table-responsive table"
                        :data="getCompFactorData"
                        header-row-class-name="thead-light"
                        fit
                        empty-text="No scenario has been run or saved."
                    >
                        <!-- Name column -->
                        <el-table-column label="Name" min-width="40px">
                            <template v-slot="{ row }">
                                <div class="table-row-name" :key="row.name">{{ row.name }}</div>
                            </template>
                        </el-table-column>

                        <!-- Message Column (Guideline) -->
                        <el-table-column label="Message" prop="message"> </el-table-column>

                        <!-- Message w/ values Column -->
                        <el-table-column label="Value" prop="valueMessage">
                            <template v-slot="{ row }">
                                <div class="allow-new-lines">{{ row.valueMessage }}</div>
                            </template>
                        </el-table-column>

                        <!-- Status Column -->
                        <el-table-column label="Status" min-width="40px" prop="status">
                            <template v-slot="{ row }">
                                <span :class="`mr-2 text-${row.variant}`"><i :class="getIcon(row.status)"></i></span>
                                <span>{{ row.status }}</span>
                            </template>
                        </el-table-column>

                        <!-- Other comp factor button column -->
                        <el-table-column min-width="40px">
                            <template v-slot="{ row }">
                                <!-- open comp factor modal -->
                                <el-dropdown trigger="click" class="dropdown" v-if="(row.name === 'Other 1' || row.name === 'Other 2') && userIsManager" @command="onCommandDropdown" :data-test="row.status">
                                    <span class="btn btn-sm btn-icon-only text-light">
                                        <i class="fas fa-ellipsis-v mt-2"></i>
                                    </span>
                                    <el-dropdown-menu class="dropdown-menu dropdown-menu-arrow show" slot="dropdown">
                                        <!-- view a comp factor with the ability to overwrite it. only available to managers. -->
                                        <el-dropdown-item v-b-modal="'comp-factor'" :command="{reset: false, name: row.name}">
                                            {{ row.valid ? "View Compensating Factor" : "Make Compensating Factor" }}
                                        </el-dropdown-item>
                                        <el-dropdown-item v-if="row.valid" :command="{reset: true, name: row.name}">
                                            Reset '{{row.name}}'
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                </b-card>
            </b-col>
        </b-row>
        <!-- other compensating factor modal -->
        <!-- TODO: make the ok button text change happen after the modal closes -->
        <b-modal
            centered
            :ok-title="getCurrentCompFactor.valid ? 'Overwrite' : 'Confirm'"
            id="comp-factor"
            size="xl"
            @ok="updateOtherCompFactor($event)"
            @hidden="
                setCurrentCompFactor(getCurrentCompFactor.name);
                setPassingCompFactorLength();
            "
            ok-variant="ao-light"
            :static="staticModal"
        >
            <h3 slot="modal-title">Set 'Other' Compensating Factor</h3>
            <OtherCompensatingFactor :disabled="false" />
        </b-modal>
        
    </b-container>
</template>
<script>
import { Table, TableColumn, DropdownMenu, DropdownItem, Dropdown } from "element-ui";
import OtherCompensatingFactor from './OtherCompensatingFactor';
import { mapGetters, mapMutations } from "vuex";
import useVuelidate from "@vuelidate/core";
import { ruleDialog, dialogMixins } from "@/mixins/dialog.js";


export default {
    name: "compensating-factor-table",
    components: {
        ["ElTable"]: Table,
        ["ElTableColumn"]: TableColumn,
        ["ElDropdown"]: Dropdown,
        ["ElDropdownItem"]: DropdownItem,
        ["ElDropdownMenu"]: DropdownMenu,
        OtherCompensatingFactor
    },
    setup() {
        return { v$: useVuelidate()}
    },
    data() {
        return {
            staticModal: false
        }
    },
    computed: {
        ...mapGetters(["getScenario", "getCurrentCompFactor", "userIsManager"]),
        getCompFactorData() {
            // gets data for compensating factor table
            this.convertRuleDates("haloScenario");
            const tableData = [];
            for (let compFactor of this.getScenario.compensatingFactors) {
                let fields = "";
                for (let field of compFactor.fields) {
                    fields = fields + `${field.name}: "${field.value}",\n`;
                }
                let status;
                let variant;
                // change the name of compensating to 'Other 1' for older loans
                if (compFactor.name === 'Other') {
                    compFactor.name = 'Other 1';
                }
                if (compFactor.valid) {
                    if (compFactor.name === 'Other 1' || compFactor.name === 'Other 2') {
                        status = "Passed";
                        variant = "primary";
                    } else {
                        status = "Passed";
                        variant = "success";
                    }
                } else {
                    status = "Failed";
                    variant = "danger";
                }
                const compFactorData = {
                    name: compFactor.name,
                    message: compFactor.message,
                    fields: fields.slice(0, -2),
                    valueMessage: compFactor.valueMessage,
                    status,
                    variant,
                    valid: compFactor.valid
                };
                tableData.push(compFactorData);
            }
            return tableData;
        },
    },
    methods: {
        ...mapMutations(["convertRuleDates", "setCurrentCompFactor", "editCurrentCompFactor", "setOtherCompFactor", "setPassingCompFactorLength", "resetOtherCompFactor"]),
        getIcon(status) {
            let thisClass = "";
            if (status === "Passed") {
                thisClass = "fas fa-check fa-md";
            } else {
                thisClass = "fas fa-times fa-lg";
            }
            return thisClass;
        },

        updateOtherCompFactor(ev) {
            // used for validatiing override comment and updating the selected rule with the override data if the rule does not have a pre-existing exception
            this.v$.$touch();
            this.alertOnInvalidFields(this.v$, ev, false);
            if (!this.v$.invalid) {
                this.editCurrentCompFactor({prop: 'valid', value: true});
                this.setOtherCompFactor(this.getCurrentCompFactor.name);
            }
        },
        
        async resetCompFactor(name) {
            const confirmation = await this.confirmReset(name);
            if (confirmation) {
                this.resetOtherCompFactor(name);
                this.setPassingCompFactorLength();
            }
        },
        
        onCommandDropdown(command) {
            this.setCurrentCompFactor(command.name);
            if (command.reset) {
                this.resetCompFactor(command.name);
            }
        }   
    },
    mixins: [ruleDialog, dialogMixins],
};
</script>

<style>
.el-table .cell {
    padding-left: 0px;
    padding-right: 0px;
    word-break: break-word;
    font-size: 14px;
}

.table-row-name {
    font-weight: 600;
}

.allow-new-lines {
    white-space: pre-line;
}
.label {
    font-size: 15px;
}
</style>